<template>
  <div class="md-stream-reader">
    <!-- 顶部问题标题区域 -->
    <div class="question-bar">
      <div class="question-text">{{ questionTitle }}</div>
      <div class="question-meta">
        <span class="tag" :class="{ finish: isFinish }">
          {{ isFinish ? '分析完成' : '生成中...' }}
        </span>
        <span class="count">共检索 {{ caseCount }} 个相关案例</span>
        <span class="time">更新时间：{{ updateTime }}</span>
      </div>
    </div>
<!-- {{ localThinkHtml }} -->
    <!-- 可折叠深度思考面板 -->
    <div v-if="rawThinkText" class="think-collapse" :class="{ expanded: thinkExpanded }">
      <div class="think-collapse-header" @click="thinkExpanded = !thinkExpanded">
        <div class="think-collapse-left">
          
          <span class="think-title">
            {{ thinkStreaming ? '深度思考中' : '深度思考完成' }}
          </span>
          <svg class="think-icon" :class="{ rotated: thinkExpanded }" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span v-if="thinkStreaming" class="think-streaming-dot"></span>
        </div>
        <!-- <span class="think-status">
          <template v-if="thinkStreaming">思考中...</template>
          <template v-else>{{ thinkExpanded ? '收起' : '展开' }}</template>
        </span> -->
      </div>
      <div v-show="thinkExpanded" class="think-collapse-body">
        <div v-html="localThinkHtml"></div>
      </div>
    </div>

    <!-- 流式markdown内容区域 -->
    <template v-if="showMarkdownBox">
      <div class="markdown-body" v-html="renderHtml"></div>
    </template>

    <!-- 悬浮弹窗 CaseHoverTip 本地筛选数据 -->
    <div
      v-if="hoverTip.show"
      class="case-hover-tip"
      :style="{ left: hoverTip.x + 'px', top: hoverTip.y + 'px' }"
      @mouseenter="clearHoverTimer"
      @mouseleave="hideHoverTip"
    >
      <div v-if="hoverTip.empty" class="tip-status">暂无该案例详情数据</div>
      <div v-else class="tip-wrap">
        <!-- 顶部操作按钮 -->
        <div class="tip-btn-group">
          <button class="tip-btn locate-btn" @click="emitCaseClick(hoverTip.caseNo)">
            <span class="btn-icon">◎</span>
            定位案例
          </button>
          <button class="tip-btn detail-btn" @click="goDetail()">
            <span class="btn-icon">◎</span>
            查看详情
          </button>
        </div>

        <h3 class="tip-title">{{ hoverTip.data.title }}</h3>
        <div class="tip-meta">{{ hoverTip.data.meta }}</div>

        <!-- Tab切换 无边框纯文字标签 -->
        <div class="tab-group">
          <span
            v-for="tab in tabOptions"
            :key="tab.key"
            class="tab-item"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </span>
        </div>

        <!-- 正文滚动区：高度限制128px -->
        <div class="tip-scroll-content">
          {{ hoverTip.data[getActiveField] }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue'
import { marked } from 'marked'
import hljs from 'highlight.js'
import 'highlight.js/styles/atom-one-light.css'
import { getApiBaseUrl } from '@/utils/env'

const apiBaseUrl = getApiBaseUrl() || ''

// 组件入参
const props = defineProps({
  questionTitle: {
    type: String,
    default: ''
  },
  caseCount: {
    type: Number,
    default: 0
  },
  groupId:{
        type:String,
        default:""
    },
  caseListData: {
    type: Array,
    default: () => []
  },
  thinkHtml: {
    type: String,
    default: ''
  },
  thinkStreaming: {
    type: Boolean,
    default: false
  },
  rawThinkText: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['streamFinish', 'caseClick', 'detail'])

// let previousTitle = ''
// watch(() => props.questionTitle, (val) => {
//   if (val && val !== previousTitle) {
//     previousTitle = val
//     rawMdText.value = ''
//     renderHtml.value = ''
//     isFinish.value = false
//     showMarkdownBox.value = false
//     startAnalyzeStream()
//   }
// })

// 监听案例列表变化，清空弹窗，防止切换数据残留旧弹窗
watch(() => props.caseListData, () => {
  hideHoverTip()
})

// 基础流式状态
const renderHtml = ref('')
const rawMdText = ref('')
const isFinish = ref(false)
const updateTime = ref('')
const showMarkdownBox = ref(false)
const thinkExpanded = ref(false)

// 本地渲染深度思考内容（使用与 SearchLoadingPanel 相同的 marked 配置）
const localThinkHtml = computed(() => {
  if (!props.rawThinkText) return ''
  return marked.parse(fixStreamMarkdown(props.rawThinkText))
})
// console.log('qwwq',props.rawThinkText,props.thinkHtml)
// 弹窗状态
const hoverTip = ref({
  show: false,
  x: 0,
  y: 0,
  caseNo: '',
  empty: false,
  data: null
})
let hoverDelayTimer = null
const activeTab = ref('aiBrief')

// Tab配置 文字与截图完全统一：案情摘要/认定事实/本院认为/裁判结果
const tabOptions = ref([
  { key: 'aiBrief', label: '案情摘要', field: 'caseSummary' },
  { key: 'courtThink', label: '认定事实', field: 'facts' },
  { key: 'fact', label: '本院认为', field: 'courtOpinion' },
  { key: 'result', label: '裁判结果', field: 'judgment' }
])

// 计算属性：根据当前激活tab获取对应数据字段
const getActiveField = computed(() => {
  const targetTab = tabOptions.value.find(item => item.key === activeTab.value)
  return targetTab ? targetTab.field : 'caseSummary'
})

// marked渲染
marked.setOptions({
  gfm: true,
  breaks: true,
  highlight(code, lang) {
    const langLabel = lang || 'text'
    let codeHtml = lang && hljs.getLanguage(lang)
      ? hljs.highlight(code, { language: lang, ignoreIllegals: true }).value
      : hljs.highlightAuto(code).value
    return `<div class="code-wrap">
      <span class="code-lang">${langLabel}</span>
      <button class="code-copy-btn">复制代码</button>
      <pre class="hljs"><code>${codeHtml}</code></pre>
    </div>`
  }
})

/**
 * 检测行是否包含表格单元格（排除代码块）
 */
const isTableLine = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return false
  if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) return false
  return trimmed.includes('|')
}

/**
 * 检测行是否是表格分隔线
 */
const isSeparatorLine = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return false
  let content = trimmed
  if (content.startsWith('|')) content = content.slice(1)
  if (content.endsWith('|')) content = content.slice(0, -1)
  const matches = content.match(/:?-{3,}:?/g)
  return matches !== null && matches.length >= 1
}

/**
 * 解析表格行
 */
const parseTableRow = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return { cells: [], count: 0 }
  let content = trimmed
  const startsWithPipe = content.startsWith('|')
  const endsWithPipe = content.endsWith('|')
  if (startsWithPipe) content = content.slice(1)
  if (endsWithPipe) content = content.slice(0, -1)
  const cells = content.split('|').map(c => c.trim())
  return { cells, count: cells.length, startsWithPipe, endsWithPipe }
}

/**
 * 修复表格行格式
 */
const fixTableRowLine = (cells, colCount) => {
  while (cells.length < colCount) cells.push('')
  if (cells.length > colCount) cells = cells.slice(0, colCount)
  return '| ' + cells.join(' | ') + ' |'
}

/**
 * 流式 Markdown 表格修复：处理分隔线缺 |、列数不齐、缺分隔线、未闭合等问题
 */
const fixStreamMarkdown = (text) => {
  if (!text) return ''
  const lines = text.split('\n')
  const result = []
  let i = 0
  let inCodeBlock = false

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
      inCodeBlock = !inCodeBlock
      result.push(line)
      i++
      continue
    }

    if (inCodeBlock) {
      result.push(line)
      i++
      continue
    }

    if (isTableLine(line)) {
      if (result.length > 0 && result[result.length - 1].trim() !== '') {
        result.push('')
      }

      const tableLines = []
      let j = i
      while (j < lines.length) {
        const tl = lines[j]
        const tTrimmed = tl.trim()
        if (tTrimmed.startsWith('```') || tTrimmed.startsWith('~~~')) break
        if (isTableLine(tl)) {
          tableLines.push(tl)
          j++
        } else if (tTrimmed === '') {
          // let hasMore = false
          let k = j + 1
          while (k < lines.length && lines[k].trim() === '') k++
          if (k < lines.length && isTableLine(lines[k])) {
            tableLines.push(tl)
            j++
          } else break
        } else break
      }

      const cleanLines = tableLines.filter(l => l.trim() !== '')
      if (cleanLines.length > 0) {
        let colCount = 0
        cleanLines.forEach(cl => {
          const parsed = parseTableRow(cl)
          colCount = Math.max(colCount, parsed.count)
          if (isSeparatorLine(cl)) {
            const matches = cl.match(/:?-{3,}:?/g)
            if (matches) colCount = Math.max(colCount, matches.length)
          }
        })
        colCount = Math.max(colCount, 1)

        let hasSeparator = false
        cleanLines.forEach(cl => { if (isSeparatorLine(cl)) hasSeparator = true })

        const headerParsed = parseTableRow(cleanLines[0])
        result.push(fixTableRowLine(headerParsed.cells, colCount))

        result.push('|' + ' :--- |'.repeat(colCount))

        const dataStart = hasSeparator ? 2 : 1
        for (let d = dataStart; d < cleanLines.length; d++) {
          const dp = parseTableRow(cleanLines[d])
          result.push(fixTableRowLine(dp.cells, colCount))
        }

        if (j < lines.length && lines[j].trim() !== '') result.push('')
        i = j
      } else {
        result.push(line)
        i++
      }
    } else {
      result.push(line)
      i++
    }
  }

  if (result.length > 0 && !inCodeBlock) {
    const last = result[result.length - 1]
    const lastTrim = last.trim()
    if (lastTrim.startsWith('|') && !lastTrim.endsWith('|')) {
      for (let k = result.length - 2; k >= 0; k--) {
        const prev = result[k]
        if (isTableLine(prev) && prev.trim().endsWith('|')) {
          const prevCols = (prev.match(/\|/g) || []).length - 1
          const currCols = (last.match(/\|/g) || []).length
          if (prevCols >= currCols) {
            result[result.length - 1] = last + ' |'.repeat(prevCols - currCols + 1)
          }
          break
        }
      }
    }
  }

  return result.join('\n')
}

const parseMd = (str) => str ? marked.parse(fixStreamMarkdown(str)) : ''

// 通用流式请求
const startStreamFetch = async (apiUrl, reqData) => {
  rawMdText.value = ''
  renderHtml.value = ''
  isFinish.value = false
  updateTime.value = ''

  // 与 startAnalyzeStream 共用同一个 abortController（先中断上一个）
  if (analyzeAbortController) {
    analyzeAbortController.abort()
    analyzeAbortController = null
  }
  analyzeAbortController = new AbortController()
  const signal = analyzeAbortController.signal

  try {
    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reqData),
      signal
    })
    if (!res.ok || !res.body) return console.error('流式接口请求失败')
    const reader = res.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let readResult
    while (!(readResult = await reader.read()).done) {
      const chunk = decoder.decode(readResult.value, { stream: true })
      rawMdText.value += chunk
      renderHtml.value = parseMd(rawMdText.value)
    }
    isFinish.value = true
    updateTime.value = new Date().toLocaleString()
    emit('streamFinish')
  } catch (err) {
    if (err.name === 'AbortError') {
      console.log('分析流式请求已取消')
      return
    }
    console.error('流式数据接收异常：', err)
  }
}

// 本地根据案号匹配案例
const findCaseByAh = (caseNo) => {
  const searchNo = caseNo.replace(/\s+/g, '')
  const target = props.caseListData.find(item => item.ah.replace(/\s+/g, '') === searchNo)
  if (!target) return null
  return {
    title: target.cause,
    meta: `${target.court} | 案号：${target.ah} | ${target.trialLevel || ''} | ${target.year}年`,
    caseSummary: target.caseSummary || '无内容',
    facts: target.facts || '无内容',
    courtOpinion: target.courtOpinion || '无内容',
    judgment: target.judgment || '无内容'
  }
}

// 鼠标移入弹窗（300ms防抖）
const openHoverTip = (event, caseNo) => {
  clearHoverTimer()
  hoverDelayTimer = setTimeout(() => {
    const rect = event.target.getBoundingClientRect()
    const tipWidth = 640
    const margin = 16
    let posX = rect.right + margin
    let posY = rect.top

    // 边界避让优化：左右最小留白16px
    if (posX + tipWidth > window.innerWidth - margin) {
      posX = rect.left - tipWidth - margin
    }
    // 顶部不贴顶
    if (posY < margin) posY = margin

    const caseInfo = findCaseByAh(caseNo)
    hoverTip.value = {
      show: true,
      x: posX,
      y: posY,
      caseNo,
      empty: !caseInfo,
      data: caseInfo
    }
    activeTab.value = 'aiBrief'
  }, 300)
}

// 关闭弹窗
const hideHoverTip = () => {
  clearHoverTimer()
  hoverTip.value.show = false
}
const clearHoverTimer = () => {
  if (hoverDelayTimer) clearTimeout(hoverDelayTimer)
}

// 点击定位案例：隐藏弹窗并通知父组件
const emitCaseClick = (caseNo) => {
  hideHoverTip()
  emit('caseClick', caseNo)
}

// 点击查看详情
const goDetail = () => {
  const caseNo = hoverTip.value
  console.log(hoverTip.value,'hoverTip')
  hideHoverTip()
  emit('detail', caseNo)
}

// 全局鼠标事件
const globalDocEvent = async (e) => {
  const target = e.target

  // 只处理自己组件（.md-stream-reader）内的事件，避免与 WorkbenchRight 的全局事件冲突
  if (!target.closest('.md-stream-reader')) return

  // 复制代码按钮
  if (target.classList.contains('code-copy-btn')) {
    const wrap = target.closest('.code-wrap')
    const text = wrap.querySelector('code').innerText
    try {
      await navigator.clipboard.writeText(text)
      target.innerText = '复制成功'
      target.style.color = '#1768ff'
    } catch {
      target.innerText = '复制失败'
    }
    setTimeout(() => {
      target.innerText = '复制代码'
      target.style.color = ''
    }, 2000)
    return
  }

  // 获取案号单元格
  let td = target.closest('td')

  // 如果点击在弹窗内，跳过案号逻辑（让弹窗内的tab切换等正常交互）
  if (target.closest('.case-hover-tip')) return

  // click事件：如果弹窗挡住了案号单元格，先隐藏再获取下方元素
  if (!td && e.type === 'click' && hoverTip.value.show) {
    hideHoverTip()
    await new Promise(r => setTimeout(r, 10))
    const elUnder = document.elementFromPoint(e.clientX, e.clientY)
    if (elUnder) {
      td = elUnder.closest('td')
    }
  }

  if (!td) return
  const tr = td.closest('tr')
  if (!tr || td !== tr.children[1]) return
  const caseNo = (td.querySelector('a')?.textContent || td.textContent || '').replace(/\s+/g, '')
  if (!caseNo) return

  if (e.type === 'mouseover') openHoverTip(e, caseNo)
  if (e.type === 'click') emitCaseClick(caseNo)
}

// 只取前4条的ah案号
const firstFourAhList = computed(() => {
  return props.caseListData
    .slice(0, 4) // 截取数组前4条
    .map(item => item.ah)
    .filter(Boolean) // 可选：过滤空值
})
let analyzeAbortController = null

// 主分析流式接口
const startAnalyzeStream = async () => {
  if (!props.questionTitle) return

  // 创建新的 AbortController
  analyzeAbortController = new AbortController()
  const signal = analyzeAbortController.signal

  // 检查是否有预置 md 内容（从历史记录恢复等场景）
  if (window.__preloadMd) {
    rawMdText.value = window.__preloadMd
    renderHtml.value = parseMd(rawMdText.value)
    isFinish.value = true
    showMarkdownBox.value = true
    updateTime.value = new Date().toLocaleString()
    window.__preloadMd = null
    emit('streamFinish')
    return
  }

  rawMdText.value = ''
  renderHtml.value = ''
  isFinish.value = false
  showMarkdownBox.value = false
  let buffer = ''
  let streamRun = true
  const token = localStorage.getItem('token')
  try {
    const res = await fetch(`${apiBaseUrl}api/DocumentSearch/GenerateCaseAnalysis`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ query: props.questionTitle, caseNumbers: firstFourAhList.value,groupId:props.groupId, stream: true }),
      signal
    })
    if (!res.ok || !res.body) return console.error('分析接口请求失败', res.status)
    const reader = res.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let readResult
    while (streamRun) {
      readResult = await reader.read()
      if (readResult.done) {
        streamRun = false
        break
      }
      const chunk = decoder.decode(readResult.value, { stream: true })
      buffer += chunk
      if (!showMarkdownBox.value) showMarkdownBox.value = true
      while (buffer.includes('\n\n')) {
        const splitIdx = buffer.indexOf('\n\n')
        const msg = buffer.slice(0, splitIdx).trim()
        buffer = buffer.slice(splitIdx + 2)
        if (!msg) continue
        if (msg === 'data:[DONE]') {
          streamRun = false
          // console.log(rawMdText.value,'rawMdText.value')
          break
        }
        if (msg.startsWith('data:')) {
          const jsonStr = msg.slice(5).trim()
          if (!jsonStr.startsWith('{') || !jsonStr.endsWith('}')) continue
          try {
            const data = JSON.parse(jsonStr)
            if (data.content) {
              rawMdText.value += data.content
              renderHtml.value = parseMd(rawMdText.value)
            }
          } catch { continue }
        }
      }
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      console.log('GenerateCaseAnalysis 请求已取消')
      return
    }
    console.error('分析流式异常：', err)
  } finally {
    isFinish.value = true
    updateTime.value = new Date().toLocaleString()
    emit('streamFinish')
  }
}

// 生命周期
onMounted(() => {
  document.addEventListener('click', globalDocEvent)
  document.addEventListener('mouseover', globalDocEvent)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', globalDocEvent)
  document.removeEventListener('mouseover', globalDocEvent)
  clearHoverTimer()
  // 组件卸载时中断 GenerateCaseAnalysis 请求
  if (analyzeAbortController) {
    analyzeAbortController.abort()
    analyzeAbortController = null
  }
})

defineExpose({
  startStreamFetch,
  startAnalyzeStream,
  cancelAnalyze() {
    if (analyzeAbortController) {
      analyzeAbortController.abort()
      analyzeAbortController = null
    }
  },
  renderHtml,
  rawMdText,
  isFinish
})
</script>

<style scoped lang="less">
.md-stream-reader {
  width: 100%;
  background: rgba(255,255,255,0.98);
  // border-radius: 16px;
  // border: 1px solid #E8EDF5;
  border-bottom: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
}

.question-bar {
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.question-text {
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
}
.question-meta {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.tag {
  padding: 3px 9px;
  background: #ecfdf5;
  color: #039855;
  border-radius: 4px;
  font-size: 12px;
  &.finish { background: #f0fdf4; }
}
.count, .time {
  font-size: 12px;
  color: #6b7280;
}

/* Markdown 基础样式 */
:deep(.markdown-body) {
  background: #fff;
  border-radius: 10px;
  padding: 18px;
  color: #374151;
  font-size: 14px;
  line-height: 26px;
}
:deep(.markdown-body p) { margin: 8px 0; }
:deep(.markdown-body h1,h2,h3,h4) {
  margin: 16px 0 8px;
  font-weight: 600;
  color: #1f2937;
  line-height: 36px;
}
:deep(.markdown-body h2) {
  position: relative;
  padding-left: 10px;
  font-size: 15px;
  margin-top: 20px;
}
:deep(.markdown-body h2::before) { display: none; }
:deep(.markdown-body ul,ol) {
  padding-left: 20px;
  margin: 8px 0;
}
:deep(.markdown-body li) { margin: 4px 0; }
:deep(.markdown-body strong) {
  color: #1f2937;
  font-weight: 600;
}
:deep(.markdown-body blockquote) {
  border-left: 3px solid #d1d5db;
  padding-left: 12px;
  color: #6b7280;
  margin: 10px 0;
  background: #f9fafb;
  padding: 6px 10px;
  border-radius: 0 6px 6px 0;
}
:deep(.markdown-body :not(pre) > code) {
  background: #f3f4f6;
  color: #374151;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}
:deep(.markdown-body table) {
  width: 100% !important;
  border-collapse: collapse !important;
  margin: 12px 0 !important;
  border: 1px solid #e5e7eb !important;
}
:deep(.markdown-body table th) {
  background: #f9fafb !important;
  padding: 8px 10px !important;
  border: 1px solid #e5e7eb !important;
  font-weight: 600;
  color: #1f2937;
}
:deep(.markdown-body table td) {
  padding: 8px 10px !important;
  border: 1px solid #e5e7eb !important;
  color: #374151;
}
:deep(.markdown-body table tr:hover td) {
  background: #f9fafb !important;
}
:deep(.markdown-body table tr th:nth-child(2)),
:deep(.markdown-body table tr td:nth-child(2)) {
  color: #1768ff !important;
  font-weight: 600;
  cursor: pointer;
}
:deep(.markdown-body hr) {
  border: none;
  height: 1px;
  background: #e5e7eb;
  margin: 16px 0;
}
/* 代码块 */
:deep(.code-wrap) {
  position: relative;
  margin: 12px 0;
}
:deep(.code-lang) {
  position: absolute;
  top: 8px;
  left: 12px;
  font-size: 11px;
  color: #6b7280;
  z-index: 2;
}
:deep(.code-copy-btn) {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 11px;
  color: #6b7280;
  background: rgba(255,255,255,.3);
  border: none;
  border-radius: 3px;
  padding: 1px 6px;
  cursor: pointer;
  &:hover {
    color: #1768ff;
    background: rgba(23, 104, 255, 0.08);
  }
}
:deep(.markdown-body pre.hljs) {
  padding: 32px 14px 14px;
  border-radius: 8px;
  font-size: 13px;
  overflow-x: auto;
  margin: 0;
  background: #f9fafb;
}

// ===================== 弹窗基础样式 =====================
.case-hover-tip {
  position: fixed;
  z-index: 9999;
  width: 640px;
  max-height: 82vh;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 28px;
  pointer-events: auto;
}
.tip-status {
  padding: 90px 0;
  text-align: center;
  font-size: 15px;
  color: #94a3b8;
}
.tip-wrap {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

/* 按钮组 */
.tip-btn-group {
  display: flex;
  gap: 20px;
}
.tip-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 28px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 14px;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
    transform: translateY(-1px);
  }
  .btn-icon {
    font-size: 17px;
  }
}

/* 标题 */
.tip-title {
  font-size: 21px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  line-height: 1.45;
}

/* 元信息 */
.tip-meta {
  font-size: 15px;
  color: #64748b;
  line-height: 1.6;
}

/* ========== 修复后 Tab 样式：文字居中 + 下划线跟随文字 ========== */
.tab-group {
  display: flex;
  width: 100%;
  border-bottom: none;
}

.tab-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 16px;
  padding: 14px 0;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: color 0.24s ease;
  border: none;
  background: transparent;
  border-radius: 0;
  line-height: 1;
}

.tab-item:hover:not(.active) {
  color: #1e293b;
  background-color: transparent;
}

.tab-item.active {
  color: #1768ff;
  font-weight: 500;
}

/* 下划线绝对居中，宽度由文字长度决定 */
.tab-item.active::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -1px;
  width: 48px;
  height: 3px;
  background: #1768ff;
  border-radius: 2px 2px 0 0;
  transform: translateX(-50%);
}

/* 滚动内容区：固定高度128px，超出滚动 */
.tip-scroll-content {
  font-family: "仿宋_GB2312", SimSun, serif;
    font-size: 16pt;
    font-weight: 400;
    line-height: 28pt;
    color: #52627B;
    white-space: pre-wrap;
    word-break: break-word;
  max-height: 128px;
  overflow-y: auto;
  padding: 4px 8px 4px 0;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    &:hover {
      background: #94a3b8;
    }
  }
}

/* ========== 可折叠深度思考面板 ========== */
.think-collapse {
  // background: #fff;
  // border: 1px solid #e8edf5;
  // border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  transition: box-shadow 0.2s ease;
}
.think-collapse:hover {
  box-shadow: 0 2px 8px rgba(36, 70, 130, 0.06);
}
.think-collapse-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}
.think-collapse-header:hover {
  // background: #f8fafc;
}
.think-collapse-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.think-icon {
  color: #94a3b8;
  transition: transform 0.25s ease;
  flex-shrink: 0;
}
.think-icon.rotated {
  transform: rotate(90deg);
}
.think-title {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}
.think-streaming-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1768ff;
  animation: thinkPulse 1.4s ease-in-out infinite;
}
@keyframes thinkPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.75); }
}
.think-status {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 400;
}
.think-collapse-body {
  padding: 14px 20px 20px;
  font-size: 14px;
  line-height: 28px;
  color: #475569;
  max-height: 54vh;
  overflow-y: auto;
  // border-top: 1px solid #f1f5f9;
}
.think-collapse-body :deep(p) {
  margin: 8px 0;
}
.think-collapse-body :deep(h3) {
  font-size: 15px;
  font-weight: 600;
  margin: 18px 0 8px;
  color: #1e293b;
}
.think-collapse-body :deep(code) {
  background: #f1f5f9;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 13px;
  color: #e11d48;
}
.think-collapse-body :deep(pre) {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  overflow-x: auto;
  margin: 12px 0;
}
.think-collapse-body :deep(pre code) {
  background: none;
  padding: 0;
  color: inherit;
}
/* 代码块样式（与 SearchLoadingPanel 一致） */
.think-collapse-body :deep(.code-wrap) {
  position: relative;
  margin: 14px 0;
}
.think-collapse-body :deep(.code-lang) {
  position: absolute;
  top: 10px;
  left: 14px;
  font-size: 12px;
  color: #86909c;
  z-index: 2;
}
.think-collapse-body :deep(.code-copy-btn) {
  position: absolute;
  top: 10px;
  right: 14px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 2;
}
.think-collapse-body :deep(.code-copy-btn:hover) {
  color: #1768ff;
  background: rgba(23, 104, 255, 0.1);
}
.think-collapse-body :deep(.hljs) {
  padding: 38px 16px 16px;
  border-radius: 10px;
  font-size: 13px;
  overflow-x: auto;
  background: #f5f7fa;
}
</style>